import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import CatalogImage from "../components/common/CatalogImage";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Modal from "../components/common/Modal";
import Table from "../components/common/Table";
import Tabs from "../components/common/Tabs";
import BulkPassengerUpload from "../components/common/BulkPassengerUpload";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccess, showSuccessAction } from "../lib/alerts";
import { getPageCopy, resolveNoticeMessage } from "../lib/apiMessages";
import { asArray, downloadBlob, formatCurrency, unwrapData } from "../lib/catalog";
import { canEditPassengers, canRequestBookingChange, canUploadPaymentProof, documentProgressBadge, formatBookingStatus, formatPaymentStatus, isAgentCancellable, isDraftBooking, storedBookingStatus, travelServiceStatus } from "../lib/bookingStatus";
import { validatePassenger } from "../lib/passengerValidation";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWriteBookings, canWriteDocuments, canWritePassengers, canAccessFinance, roleFromProfile } from "../lib/agencyAccess";

function mapPackageItineraryItems(items = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    ...item,
    day_label: item.day_label || item.u_pkg_itry_header || `Day ${index + 1}`,
    city: item.city || item.u_pkg_itry_city || "",
    places: looksLikeHtml(item.places || item.u_pkg_itry_text) ? "" : (item.places || ""),
    notes: item.notes || item.u_pkg_itry_text || item.places || "",
  }));
}

function occupancyRoomsFromDefaults(data) {
  const booking = data?.booking || {};
  const mapping = [
    ["Single", booking.upbu_no_single_share],
    ["Twin", booking.upbu_no_twin_share],
    ["Triple", booking.upbu_no_triple_share],
    ["Quad", booking.upbu_no_quad_share],
    ["Quint", booking.upbu_no_quint_share],
    ["Hexa", booking.upbu_no_hexa_share],
  ];
  return mapping
    .filter(([, count]) => Number(count) > 0)
    .map(([label, count]) => ({
      room_label: label,
      occupancy_type: label.toLowerCase(),
      room_count: Number(count),
    }));
}

function formatDate(value) {
  if (value == null || value === "") return "";
  const text = String(value).trim();
  if (!text || text === "-" || text.startsWith("0000-00-00")) return "";
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(`${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function firstValue(source, keys = []) {
  for (const key of keys) {
    const value = source?.[key];
    if (value != null && value !== "") return value;
  }
  return "";
}

function displayValue(value, fallback = "-") {
  if (value == null || value === "") return fallback;
  return value;
}

function formatLabel(value, fallback = "-") {
  const text = String(value ?? "").trim();
  if (!text || text === "-") return fallback;
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const OCCUPANCY_LABELS = {
  single: "Single",
  twin: "Twin",
  triple: "Triple",
  quad: "Quad",
  quint: "Quint",
  hexa: "Hexa",
  cwb: "CWB",
  cwob: "CWOB",
  infnt: "Infant",
  extra: "Extra",
};

function occupancyDisplay(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "-") return "";
  return OCCUPANCY_LABELS[text.toLowerCase()] || formatLabel(text, text);
}

function activityEventLabel(item) {
  return formatLabel(firstValue(item, ["event_type", "activity", "title", "event_name"]));
}

function activityDateTime(item) {
  const value = firstValue(item, [
    "created_date",
    "event_at",
    "created_at",
    "event_time",
    "timestamp",
    "logged_at",
    "updated_date",
  ]);
  if (!value) return "-";
  return formatDateTime(value) || formatDate(value) || "-";
}

function activityActor(item) {
  return formatLabel(
    firstValue(item, [
      "actor_role",
      "actor_type",
      "actor",
      "user",
      "user_name",
      "created_by",
      "performed_by",
      "source",
      "system",
    ])
  );
}

function activityStatus(item) {
  return firstValue(item, ["status_to", "status", "event_status", "status_from"]);
}

function hasFilledValue(value) {
  const text = String(value ?? "").trim();
  return text !== "" && text !== "-" && text !== "0000-00-00";
}

function itineraryDateValue(item) {
  return firstValue(item, ["travel_date", "travelDate", "date", "itinerary_date", "day_date"]);
}

function itineraryTimeValue(item) {
  const time = firstValue(item, ["travel_time", "travelTime", "time"]);
  return time ? String(time).slice(0, 5) : "";
}

function itineraryDateDisplay(item) {
  const dateValue = itineraryDateValue(item);
  const timeValue = itineraryTimeValue(item);
  if (!hasFilledValue(dateValue) && !hasFilledValue(timeValue)) return "";
  if (hasFilledValue(dateValue) && hasFilledValue(timeValue)) {
    return `${formatDate(dateValue)} ${timeValue}`;
  }
  if (hasFilledValue(dateValue)) return formatDate(dateValue);
  return timeValue;
}

function itineraryHotelValue(item) {
  return firstValue(item, ["hotel", "hotel_name"]);
}

function itineraryTransportValue(item) {
  return firstValue(item, ["transport_information", "transportInformation", "transport", "transport_type"]);
}

function looksLikeHtml(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

function itineraryActivityValue(item) {
  const value = firstValue(item, ["activity", "places"]);
  if (!hasFilledValue(value) || looksLikeHtml(value)) return "";
  return value;
}

function itineraryNotesHtml(item) {
  const notes = firstValue(item, ["notes", "description", "remarks"]);
  const places = firstValue(item, ["places", "u_pkg_itry_text"]);
  if (hasFilledValue(notes)) return notes;
  if (looksLikeHtml(places)) return places;
  return "";
}

function itineraryColumnHasValue(items, getter) {
  return items.some((item) => hasFilledValue(getter(item)));
}

const HTML_ENTITIES = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  ndash: "–",
  mdash: "—",
};

function stripHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (_, entity) => {
      const key = String(entity).toLowerCase();
      if (HTML_ENTITIES[key]) return HTML_ENTITIES[key];
      if (key.startsWith("#x")) {
        const code = Number.parseInt(key.slice(2), 16);
        return Number.isNaN(code) ? " " : String.fromCharCode(code);
      }
      if (key.startsWith("#")) {
        const code = Number.parseInt(key.slice(1), 10);
        return Number.isNaN(code) ? " " : String.fromCharCode(code);
      }
      return " ";
    })
    .replace(/\s+/g, " ")
    .trim();
}

function previewText(value, maxLength = 42) {
  const text = stripHtml(value);
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

function notesToBlocks(value) {
  const paragraphs = String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;|&amp;nbsp;|&#160;|&#x0*a0;/gi, " ")
    .replace(/[\u00a0\u202f]/g, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&ndash;/gi, "–")
    .replace(/&mdash;/gi, "—")
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  if (!paragraphs.length) return { heading: "", paragraphs: [] };

  const [first, ...rest] = paragraphs;
  if (first.length <= 80 && rest.length) {
    return { heading: first, paragraphs: rest };
  }
  return { heading: "", paragraphs };
}

function recordList(...candidates) {
  for (const value of candidates) {
    if (Array.isArray(value) && value.length > 0) return value;
  }
  return [];
}

function joinRoute(...parts) {
  return parts.filter((part) => {
    const text = String(part || "").trim();
    return text && text !== "-" && !/^\d+$/.test(text);
  }).join(" → ");
}

function placeValue(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "-" || /^\d+$/.test(text)) return "";
  return text;
}

function combineDateTime(dateValue, timeValue) {
  if (dateValue && timeValue) {
    const parsed = new Date(`${dateValue} ${timeValue}`);
    if (!Number.isNaN(parsed.getTime())) return formatDateTime(parsed);
    return `${formatDate(dateValue)} ${timeValue}`;
  }
  if (dateValue) {
    const text = String(dateValue);
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime()) && /T|\d{2}:\d{2}/.test(text)) {
      return formatDateTime(dateValue);
    }
    if (text.includes("T")) return formatDateTime(dateValue) || dateValue;
    if (!Number.isNaN(parsed.getTime())) return formatDate(dateValue);
    return text;
  }
  return timeValue || "-";
}

function documentStatus(summary) {
  if (summary?.documents?.all_complete) return { value: "complete", label: "Complete" };
  if (Number(summary?.documents?.action_required) > 0) {
    return { value: "action_required", label: "Action required" };
  }
  return documentProgressBadge({
    actionRequiredCount: summary?.documents?.action_required,
    completeCount: summary?.documents?.complete_count ?? summary?.documents?.complete,
    passengerCount: summary?.documents?.total,
  });
}

function visaStatus(summary) {
  if (summary?.visas?.complete) return { value: "issued", label: "Issued" };
  if (summary?.visas?.action_required) return { value: "action_required", label: "Action required" };
  if (summary?.visas?.issued) return { value: "in_progress", label: "In progress" };
  return { value: "pending", label: "Pending" };
}

function pilgrimDocumentStatus(pilgrim) {
  if (Number(pilgrim.action_required_documents) > 0) return "action_required";
  return pilgrim.latest_document_status || "pending";
}

function isImageFile(mimeType, fileName = "") {
  return String(mimeType || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp)$/i.test(fileName);
}

function getFlightRecords(data, travel, packageDefaults) {
  const onwardDefault = packageDefaults?.flightBlock?.onward || {};
  const returnDefault = packageDefaults?.flightBlock?.return || {};
  const rawList = recordList(data?.flights, travel?.flights);
  const onwardRow = rawList.find((row) => !String(row.leg || row.leg_label || "").toLowerCase().includes("return")) || rawList[0] || {};
  const onwardDep = placeValue(travel.departure_airport) || placeValue(travel.flight?.departure_airport) || placeValue(onwardRow.departure_airport) || placeValue(onwardDefault.departure_airport_name) || placeValue(onwardDefault.fbo_departure);
  const onwardArr = placeValue(travel.arrival_airport) || placeValue(travel.flight?.arrival_airport) || placeValue(onwardRow.arrival_airport) || placeValue(onwardDefault.arrival_airport_name) || placeValue(onwardDefault.fbo_arrival);
  const returnDep = placeValue(travel.return_departure_airport) || placeValue(travel.return_flight?.departure_airport) || placeValue(returnDefault.departure_airport_name) || placeValue(returnDefault.fbr_departure) || onwardArr;
  const returnArr = placeValue(travel.return_arrival_airport) || placeValue(travel.return_flight?.arrival_airport) || placeValue(returnDefault.arrival_airport_name) || placeValue(returnDefault.fbr_arrival) || onwardDep;

  const list = rawList.map((row) => {
    const isReturn = String(row.leg || row.leg_label || "").toLowerCase().includes("return");
    return {
      ...row,
      airline: placeValue(firstValue(row, ["airline", "airline_name", "flight_airline_name"])) || placeValue(isReturn ? travel.return_airline_name : travel.flight_airline_name) || placeValue(isReturn ? returnDefault.fbr_airlines || returnDefault.fam_airline_name : onwardDefault.fbo_airlines || onwardDefault.fam_airline_name),
      flight_number: firstValue(row, ["flight_number", "flight_code", "flight_no"]) || (isReturn ? travel.return_flight_number : travel.flight_number),
      departure_airport: placeValue(row.departure_airport) || (isReturn ? returnDep : onwardDep),
      arrival_airport: placeValue(row.arrival_airport) || (isReturn ? returnArr : onwardArr),
      departure_datetime: isReturn ? (travel.return_departure_datetime || row.departure_datetime) : (travel.departure_datetime || row.departure_datetime),
      arrival_datetime: isReturn ? (travel.return_arrival_datetime || row.arrival_datetime) : (travel.arrival_datetime || row.arrival_datetime),
      pnr: firstValue(row, ["pnr", "flight_pnr"]) || (isReturn ? travel.return_pnr : travel.pnr) || travel.pnr,
    };
  });
  const hasReturn = list.some((row) => String(row.leg || row.leg_label || "").toLowerCase().includes("return"));

  if (!list.length && (travel.flight_airline_name || travel.flight_number || travel.pnr || onwardDep || onwardDefault.fbo_flight_id)) {
    list.push({
      leg: "onward",
      leg_label: "Onward",
      airline: placeValue(travel.flight_airline_name) || placeValue(onwardDefault.fbo_airlines),
      flight_number: travel.flight_number || onwardDefault.fbo_flight_id || onwardDefault.fbo_code,
      departure_airport: onwardDep,
      arrival_airport: onwardArr,
      departure_datetime: travel.departure_datetime,
      arrival_datetime: travel.arrival_datetime,
      departure_date: travel.flight_departure_date || travel.departure_datetime || onwardDefault.fbo_departure_date,
      departure_time: travel.flight_departure_time || travel.departure_time || onwardDefault.fbo_departure_time,
      arrival_date: travel.flight_arrival_date || travel.arrival_datetime || onwardDefault.fbo_arrival_date,
      arrival_time: travel.flight_arrival_time || travel.arrival_time || onwardDefault.fbo_arrival_time,
      pnr: travel.pnr || packageDefaults?.flightBlock?.fb_pnr,
      status: travel.flight_status,
    });
  }

  if (!hasReturn && (travel.return_flight_number || travel.return_airline_name || travel.return_pnr || returnDep || returnDefault.fbr_flight_id || returnDefault.fbr_code)) {
    list.push({
      leg: "return",
      leg_label: "Return",
      airline: placeValue(travel.return_airline_name) || placeValue(returnDefault.fbr_airlines) || placeValue(returnDefault.fam_airline_name) || placeValue(travel.flight_airline_name),
      flight_number: travel.return_flight_number || returnDefault.fbr_flight_id || returnDefault.fbr_code,
      departure_airport: returnDep,
      arrival_airport: returnArr,
      departure_datetime: travel.return_departure_datetime,
      arrival_datetime: travel.return_arrival_datetime,
      departure_date: travel.return_departure_date || travel.return_departure_datetime || returnDefault.fbr_departure_date,
      departure_time: travel.return_departure_time || returnDefault.fbr_departure_time,
      arrival_date: travel.return_arrival_date || travel.return_arrival_datetime || returnDefault.fbr_arrival_date,
      arrival_time: travel.return_arrival_time || returnDefault.fbr_arrival_time,
      pnr: travel.return_pnr || travel.pnr || packageDefaults?.flightBlock?.fb_pnr,
      status: travel.return_flight_status || travel.flight_status,
    });
  }

  return list.map((row) => {
    const isReturn = String(row.leg || row.leg_label || "").toLowerCase().includes("return");
    return {
      ...row,
      departure_airport: placeValue(row.departure_airport) || (isReturn ? returnDep : onwardDep),
      arrival_airport: placeValue(row.arrival_airport) || (isReturn ? returnArr : onwardArr),
      status: travelServiceStatus(
        row.airline || row.flight_number,
        row.status || travel.flight_status
      ),
    };
  });
}

function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

function nightsBetween(checkIn, checkOut) {
  const start = parseDateOnly(checkIn);
  const end = parseDateOnly(checkOut);
  if (!start || !end) return "";
  const nights = Math.round((end.getTime() - start.getTime()) / 86400000);
  return nights > 0 ? nights : "";
}

function hotelDisplayStatus(name, status) {
  return travelServiceStatus(name, status);
}

function cityLooksLike(value, city) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return false;
  if (city === "makkah") return text.includes("makkah") || text.includes("mecca") || text === "279";
  if (city === "madinah") return text.includes("madin") || text === "278" || text === "280";
  return text.includes(city);
}

function catalogNameKey(name) {
  return String(name || "").trim().toLowerCase();
}

function pickCatalogHotel(items, name) {
  const needle = catalogNameKey(name);
  if (!needle || !Array.isArray(items) || !items.length) return null;
  return items.find((item) => catalogNameKey(item.name || item.uhl_hotel_name) === needle)
    || items.find((item) => catalogNameKey(item.name || item.uhl_hotel_name).includes(needle))
    || items.find((item) => needle.includes(catalogNameKey(item.name || item.uhl_hotel_name)))
    || items[0]
    || null;
}

async function loadHotelCatalog(hotels = []) {
  const uniqueNames = [...new Set(hotels.map((hotel) => hotel.hotel_name).filter(Boolean))];
  const entries = await Promise.all(uniqueNames.map(async (name) => {
    try {
      const payload = await agentApi.get("/umrah-hotels", {
        params: { search: name, limit: 8 },
      });
      const match = pickCatalogHotel(asArray(unwrapData(payload)), name);
      return [catalogNameKey(name), match];
    } catch (error) {
      return [catalogNameKey(name), null];
    }
  }));
  return Object.fromEntries(entries.filter(([, match]) => match));
}

function defaultHotelForCity(defaults = [], city) {
  return defaults.find((item) => cityLooksLike(item.city_name || item.uphp_city || item.city, city)) || null;
}

function getHotelRecords(data, travel, packageDefaults = {}, rooms = []) {
  const hotelDetails = travel.hotel || {};
  const defaults = Array.isArray(packageDefaults?.hotels) ? packageDefaults.hotels : [];
  const roomCount = rooms.reduce((sum, room) => sum + Number(room.room_count || 0), 0) || "";
  const occupancy = rooms[0]?.occupancy_type || travel.occupancy_type || hotelDetails.occupancy_type;

  const citySpecs = {
    Makkah: {
      name: travel.makkah_hotel_name || hotelDetails.makkah_hotel_name,
      image: travel.makkah_hotel_image || hotelDetails.makkah_hotel_image,
      checkIn: travel.makkah_check_in || hotelDetails.makkah_check_in,
      checkOut: travel.makkah_check_out || hotelDetails.makkah_check_out,
      nights: travel.makkah_nights || hotelDetails.makkah_nights,
      roomType: travel.makkah_room_type || hotelDetails.makkah_room_type,
      occupancyType: travel.makkah_occupancy_type || hotelDetails.makkah_occupancy_type || occupancy,
      mealPlan: travel.makkah_meal_plan || hotelDetails.makkah_meal_plan || travel.meal_plan || hotelDetails.meal_plan,
      confirmation: travel.makkah_confirmation_number || hotelDetails.makkah_confirmation_number || travel.hotel_confirmation_number,
      rooms: travel.makkah_rooms || travel.room_count || roomCount,
      status: travel.makkah_hotel_status || travel.hotel_status || travel.hotel_confirmation_status,
      fallback: defaultHotelForCity(defaults, "makkah"),
    },
    Madinah: {
      name: travel.madinah_hotel_name || hotelDetails.madinah_hotel_name,
      image: travel.madinah_hotel_image || hotelDetails.madinah_hotel_image,
      checkIn: travel.madinah_check_in || hotelDetails.madinah_check_in,
      checkOut: travel.madinah_check_out || hotelDetails.madinah_check_out,
      nights: travel.madinah_nights || hotelDetails.madinah_nights,
      roomType: travel.madinah_room_type || hotelDetails.madinah_room_type,
      occupancyType: travel.madinah_occupancy_type || hotelDetails.madinah_occupancy_type || occupancy,
      mealPlan: travel.madinah_meal_plan || hotelDetails.madinah_meal_plan || travel.meal_plan || hotelDetails.meal_plan,
      confirmation: travel.madinah_confirmation_number || hotelDetails.madinah_confirmation_number,
      rooms: travel.madinah_rooms || travel.room_count || roomCount,
      status: travel.madinah_hotel_status || travel.hotel_status || travel.hotel_confirmation_status,
      fallback: defaultHotelForCity(defaults, "madinah"),
    },
  };

  function enrich(row, cityFallback) {
    const guessedCity = /madin/i.test(String(firstValue(row, ["city", "location", "city_name"]) || cityFallback)) ? "Madinah" : "Makkah";
    const spec = citySpecs[guessedCity] || {};
    const name = firstValue(row, ["hotel_name", "name", "hotel", "uphp_hotel"]) || spec.name || spec.fallback?.uphp_hotel;
    const checkIn = firstValue(row, ["check_in", "checkIn"]) || spec.checkIn;
    const checkOut = firstValue(row, ["check_out", "checkOut"]) || spec.checkOut;
    const nights = firstValue(row, ["nights", "night_count", "no_of_nights", "uphp_no_of_night"])
      || spec.nights
      || nightsBetween(checkIn, checkOut)
      || spec.fallback?.uphp_no_of_night
      || spec.fallback?.nights;
    const catalog = (data?.hotel_catalog || {})[catalogNameKey(name)] || {};
    const image = firstValue(row, ["image", "hotel_image", "photo", "file_name", "uhl_image"])
      || spec.image
      || spec.fallback?.hotel_image
      || spec.fallback?.uhl_image
      || spec.fallback?.image
      || catalog.image
      || catalog.uhl_image;
    return {
      ...row,
      hotel_name: name,
      city: firstValue(row, ["city", "location", "city_name"]) || guessedCity,
      image,
      stars: firstValue(row, ["stars", "uhl_stars"]) || catalog.stars,
      address: firstValue(row, ["address", "uhl_address"]) || catalog.address,
      property: firstValue(row, ["property", "uhl_property", "propertyType"]) || catalog.propertyType,
      check_in: checkIn,
      check_out: checkOut,
      nights,
      room_type: firstValue(row, ["room_type"]) || spec.roomType,
      occupancy_type: firstValue(row, ["occupancy_type", "occupancyType"]) || spec.occupancyType,
      rooms: firstValue(row, ["rooms", "room_count"]) || spec.rooms,
      guests: firstValue(row, ["guests", "guest_count", "no_of_guests"]),
      meal_plan: firstValue(row, ["meal_plan", "mealPlan"]) || spec.mealPlan,
      confirmation: firstValue(row, ["confirmation", "confirmation_number", "confirmation_no", "reference", "hotel_reference"]) || spec.confirmation,
      status: hotelDisplayStatus(name, firstValue(row, ["confirmation_status", "status", "hotel_status"]) || spec.status),
    };
  }

  const list = recordList(data?.hotels, travel?.hotels).map((hotel, index) => {
    const city = firstValue(hotel, ["city", "location", "city_name"]);
    return enrich(hotel, /madin/i.test(city) || (!city && index === 1) ? "Madinah" : "Makkah");
  });
  if (list.length) return list;

  return Object.entries(citySpecs)
    .filter(([, spec]) => spec.name || spec.fallback?.uphp_hotel || spec.image || spec.checkIn || spec.checkOut)
    .map(([city, spec]) => enrich({
      hotel_name: spec.name,
      city,
      image: spec.image,
      check_in: spec.checkIn,
      check_out: spec.checkOut,
      nights: spec.nights,
      room_type: spec.roomType,
      occupancy_type: spec.occupancyType,
      rooms: spec.rooms,
      meal_plan: spec.mealPlan,
      confirmation: spec.confirmation,
      status: spec.status,
    }, city));
}

function normalizeTransportRow(row = {}, fallback = {}) {
  const pickup = firstValue(row, ["pickup", "pickup_point", "transport_pickup_point"]) || firstValue(fallback, ["transport_pickup_point", "package_transport_city"]);
  const drop = firstValue(row, ["drop", "drop_point", "transport_drop_point"]) || firstValue(fallback, ["transport_drop_point"]);
  const datetime = firstValue(row, ["datetime", "transport_pickup_datetime", "pickup_datetime", "date", "transport_date", "pickup_date"]) || firstValue(fallback, ["transport_pickup_datetime"]);
  const driver = firstValue(row, ["driver", "driver_name", "transport_driver_name", "transport_driver"]) || firstValue(fallback, ["transport_driver_name"]);
  const vendor = firstValue(row, ["provider", "transport_provider", "vendor", "vendor_name", "transport_vendor_name", "vehicle_name"]) || firstValue(fallback, ["transport_vendor_name", "package_transport_name"]);
  const vehicleType = firstValue(row, ["vehicle_type", "transport_vehicle_type"]) || firstValue(fallback, ["transport_vehicle_type", "package_transport_vehicle"]);
  const transportType = firstValue(row, ["transport_type", "service"]) || firstValue(fallback, ["transport_type", "package_transport_type"]);
  const vehicleNumber = firstValue(row, ["reference", "transport_reference", "vehicle_number", "transport_vehicle_number", "confirmation", "booking_reference"]) || firstValue(fallback, ["transport_vehicle_number"]);

  return {
    ...row,
    vehicle: firstValue(row, ["vehicle", "vehicle_name"]) || vendor || vehicleType || transportType,
    pickup,
    drop,
    date: datetime,
    driver: driver || vendor,
    reference: vehicleNumber,
    status: travelServiceStatus(
      vendor || vehicleType || transportType,
      firstValue(row, ["status", "transport_status"]) || firstValue(fallback, ["transport_status"])
    ),
  };
}

function getTransportRecords(data, travel, packageDefaults = {}) {
  const nested = travel?.transport && typeof travel.transport === "object" && !Array.isArray(travel.transport)
    ? travel.transport
    : {};
  const source = { ...packageDefaults, ...nested, ...travel };
  const list = recordList(data?.transports, data?.transport, travel?.transports);
  if (list.length) return list.map((row) => normalizeTransportRow(row, source));
  if (source.transport_type || source.package_transport_type || source.transport_pickup_point || source.transport_drop_point || source.transport_vendor_name || source.transport_driver_name || source.transport_vehicle_type || source.package_transport_name || source.package_transport_vehicle || source.transport_pickup_datetime) {
    return [normalizeTransportRow(source, source)];
  }
  return [];
}

function joinMeta(...parts) {
  return parts.filter((part) => part != null && part !== "" && part !== "-").join(" · ");
}

function FieldBlock({ label, children }) {
  return (
    <div className="agent-booking-field">
      <div className="agent-booking-field-label">{label}</div>
      <div className="agent-booking-field-value">{children}</div>
    </div>
  );
}

function SummaryTile({ label, children }) {
  return (
    <div className="agent-booking-tile h-100">
      <div className="agent-booking-field-label">{label}</div>
      {children}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="agent-booking-empty">
      {message}
    </div>
  );
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: "passport", label: "Passport" },
  { value: "photo", label: "Photo" },
  { value: "supporting_document", label: "Supporting document" },
];

const BOOKING_TABS = [
  { id: "overview", label: "Overview" },
  { id: "flights", label: "Flights" },
  { id: "hotels", label: "Hotels" },
  //{ id: "transport", label: "Transport" },
  { id: "pilgrims", label: "Pilgrims" },
  { id: "itinerary", label: "Itinerary" },
  { id: "payment", label: "Payment" },
  { id: "voucher", label: "Voucher" },
  { id: "activity", label: "Activity" },
  { id: "requests", label: "Requests" },
];

const emptyPassenger = {
  uppd_title: "Mr",
  uppd_fname: "",
  uppd_lname: "",
  uppd_gender: "Male",
  uppd_dob: "",
  uppd_passport_no: "",
  uppd_passport_issue_date: "",
  uppd_passport_expiry: "",
  uppd_passport_issue_place: "",
  uppd_adult_type: "adult",
  uppd_image: null,
};

const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

function isAllowedDocumentFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return ["application/pdf", "image/jpeg", "image/png"].includes(type) || /\.(pdf|jpe?g|png)$/.test(name);
}

function isAllowedPhotoFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return type.startsWith("image/") || /\.(jpe?g|png)$/.test(name);
}

export default function BookingDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadForm, setUploadForm] = useState({ documentType: "", file: null });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlsRef = useRef([]);
  const [passengerForm, setPassengerForm] = useState(emptyPassenger);
  const [savingPassenger, setSavingPassenger] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [removingPassengerId, setRemovingPassengerId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [notesPreview, setNotesPreview] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [requestHistory, setRequestHistory] = useState([]);
  const [requestModal, setRequestModal] = useState(null);
  const [requestForm, setRequestForm] = useState({ reason: "", changes: "" });
  const [savingRequest, setSavingRequest] = useState(false);
  const [agencyRole, setAgencyRole] = useState(null);

  useEffect(() => {
    const notice = resolveNoticeMessage(location.state?.noticeAction || location.state?.notice);
    if (notice) {
      showSuccess(notice);
    }
  }, [location.state?.notice, location.state?.noticeAction]);

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => window.URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
  }, []);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [payload, profile] = await Promise.all([
          agentApi.get(`/travel-partner/bookings/${id}/journey`),
          fetchCurrentAgent().catch(() => null),
        ]);
        if (!alive) return;
        setAgencyRole(roleFromProfile(profile));
        const journey = payload?.data || payload || {};
        setData(journey);
        const assignedHotels = getHotelRecords(journey, journey.operation || {}, journey.package_defaults, journey.rooms || []);
        const hotelCatalog = await loadHotelCatalog(assignedHotels);
        if (!alive) return;
        setData({ ...journey, hotel_catalog: hotelCatalog });
        try {
          const [summaryPayload, historyPayload] = await Promise.all([
            agentApi.get(`/payments/booking/${id}/summary`),
            agentApi.get(`/payments/booking/${id}/history`),
          ]);
          if (!alive) return;
          setPaymentSummary(summaryPayload?.data?.summary || summaryPayload?.data || null);
          const historyData = historyPayload?.data || historyPayload || {};
          setPaymentHistory(Array.isArray(historyData.payments) ? historyData.payments : Array.isArray(historyData.data) ? historyData.data : Array.isArray(historyData) ? historyData : []);
        } catch (paymentError) {
          if (alive) {
            setPaymentSummary(null);
            setPaymentHistory([]);
          }
        }
        try {
          const requestPayload = await agentApi.get(`/travel-partner/bookings/${id}/change-requests`);
          if (!alive) return;
          const requestData = unwrapData(requestPayload);
          const rows = asArray(requestData);
          setChangeRequests(rows);
          const latest = rows[0];
          if (latest?.request_id) {
            const detail = await agentApi.get(`/travel-partner/change-requests/${latest.request_id}`);
            const detailData = unwrapData(detail);
            setRequestHistory(asArray(detailData.history || detailData));
          } else {
            setRequestHistory([]);
          }
        } catch (_requestError) {
          if (alive) {
            setChangeRequests([]);
            setRequestHistory([]);
          }
        }
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_BOOKING");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [id]);

  const booking = {
    ...(data?.booking || {}),
    booking_status: storedBookingStatus(data?.booking || {}),
    lifecycle_state: data?.booking?.lifecycle_state || storedBookingStatus(data?.booking || {})
  };
  const pilgrims = Array.isArray(data?.pilgrims) ? data.pilgrims : [];
  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
  const travel = data?.operation || {};
  const readiness = data?.readiness || {};
  const summary = data?.summary || {};
  const rooms = Array.isArray(data?.rooms) && data.rooms.length
    ? data.rooms
    : occupancyRoomsFromDefaults(data || {});
  const itinerary = Array.isArray(data?.itinerary) && data.itinerary.length
    ? data.itinerary
    : mapPackageItineraryItems(data?.package_defaults?.itinerary);
  const showItineraryDate = itineraryColumnHasValue(itinerary, itineraryDateDisplay);
  const showItineraryActivity = itineraryColumnHasValue(itinerary, itineraryActivityValue);
  const showItineraryHotel = itineraryColumnHasValue(itinerary, itineraryHotelValue);
  const showItineraryTransport = itineraryColumnHasValue(itinerary, itineraryTransportValue);
  const flights = getFlightRecords(data, travel, data?.package_defaults);
  const hotels = getHotelRecords(data, travel, data?.package_defaults, rooms);
  const transports = getTransportRecords(data, travel, data?.package_defaults);
  
  const docs = documentStatus(summary);
  const visas = visaStatus(summary);
  const paymentStatus = paymentSummary?.payment_status || paymentSummary?.paymentStatus || summary.payment?.status || "pending";
  const voucherStatus = travel.voucher_status || "not_published";
  const voucherPublished = String(voucherStatus).toLowerCase().replace(/[\s-]+/g, "_") === "published";
  const pilgrimLimit = Number(booking.upbu_no_of_passengers ?? booking.no_of_passengers ?? booking.booking_pilgrim_count) || 0;
  const canMutateBookings = agencyRole != null && canWriteBookings(agencyRole);
  const canMutatePassengers = agencyRole != null && canWritePassengers(agencyRole);
  const canMutateDocuments = agencyRole != null && canWriteDocuments(agencyRole);
  const canMutateFinance = agencyRole != null && canAccessFinance(agencyRole);
  const statusAllowsPassengerEdit = canEditPassengers(booking.booking_status);
  const canAddPilgrim = canMutatePassengers && statusAllowsPassengerEdit && (pilgrimLimit <= 0 || pilgrims.length < pilgrimLimit);
  const remainingSlots = pilgrimLimit > 0 ? Math.max(pilgrimLimit - pilgrims.length, 0) : 0;
  const pilgrimCountLabel = pilgrimLimit > 0 ? `${pilgrims.length} / ${pilgrimLimit}` : String(booking.booking_pilgrim_count || pilgrims.length || 0);
  const bookedOn = firstValue(booking, ["booking_date", "upbu_booking_date", "created_at"]);
  const returnDate = firstValue(booking, ["return_date", "upbu_return_date", "arrival_date"]);
  const bookingTotal = paymentSummary?.bookingTotal ?? paymentSummary?.booking_total ?? paymentSummary?.total ?? paymentSummary?.total_amount ?? summary.payment?.total ?? booking.upbu_total ?? booking.total_amount;
  const outstandingAmount = paymentSummary?.outstanding;
  const flightStatus = travelServiceStatus(
    travel.flight_airline_name || travel.flight_number,
    firstValue(travel, ["flight_status", "flight_confirmation_status"])
  );
  const hotelStatus = hotelDisplayStatus(
    travel.makkah_hotel_name || travel.madinah_hotel_name || hotels[0]?.hotel_name,
    firstValue(travel, ["hotel_status", "hotel_confirmation_status", "makkah_hotel_status"]) || hotels[0]?.status
  );
  const transportStatus = hotelDisplayStatus(
    travel.transport_type || travel.transport_pickup_point || travel.transport_vendor_name || transports[0]?.vehicle,
    firstValue(travel, ["transport_status"]) || transports[0]?.status
  );
  const flightMeta = joinMeta(travel.flight_airline_name, travel.flight_number, travel.return_flight_number ? `Return ${travel.return_flight_number}` : "") || "Pending assignment";
  const hotelMeta = joinMeta(travel.makkah_hotel_name, travel.madinah_hotel_name) || "Pending assignment";
  const transportMeta = joinMeta(
    travel.transport_vendor_name || travel.transport_vehicle_type || transports[0]?.vehicle,
    joinRoute(travel.transport_pickup_point, travel.transport_drop_point)
  ) || travel.transport_type || "Pending assignment";
  const voucherMeta = travel.voucher_title || (voucherPublished ? `Travel Voucher – ${booking.booking_reference || id}` : "Not published yet");

  function resetUploadModal() {
    setUploadTarget(null);
    setUploadForm({ documentType: "", file: null });
    setUploading(false);
    setUploadProgress(0);
  }

  function closeUploadModal() {
    if (uploading) return;
    resetUploadModal();
  }

  function openUploadModal(pilgrim) {
    setUploadForm({ documentType: "", file: null });
    setUploadProgress(0);
    setUploadTarget({
      passengerId: pilgrim.passenger_id,
      passengerName: pilgrim.passenger_name,
    });
  }

  function revokePreviewUrls() {
    previewUrlsRef.current.forEach((url) => window.URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
  }

  function closePreviewModal() {
    revokePreviewUrls();
    setPreviewTarget(null);
    setPreviewFiles([]);
    setPreviewIndex(0);
    setPreviewLoading(false);
  }

  async function openPreviewModal(pilgrim) {
    revokePreviewUrls();
    setPreviewTarget({ passengerId: pilgrim.passenger_id });
    setPreviewFiles([]);
    setPreviewIndex(0);
    setPreviewLoading(true);
    try {
      const payload = await agentApi.get(`/travel-partner/bookings/${id}/pilgrims/${pilgrim.passenger_id}/documents`);
      const docs = payload?.data?.documents || payload?.documents || [];
      const list = Array.isArray(docs) ? docs : [];
      if (!list.length) {
        closePreviewModal();
        showErrorAction("NO_DOCUMENTS");
        return;
      }

      const files = await Promise.all(list.map(async (doc) => {
        const blob = await agentApi.get(`/travel-partner/documents/${doc.document_id}/download`, { responseType: "blob" });
        const url = window.URL.createObjectURL(blob);
        previewUrlsRef.current.push(url);
        return {
          url,
          mimeType: blob.type || "",
          fileName: doc.file_name || doc.original_file_name || `document-${doc.document_id}`,
        };
      }));
      setPreviewFiles(files);
    } catch (previewError) {
      closePreviewModal();
      showApiError(previewError, "PREVIEW_DOCUMENT");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function uploadDocument() {
    if (!canMutateDocuments) return;
    const passengerId = uploadTarget?.passengerId;
    if (!passengerId) return;
    if (!uploadForm.file || !uploadForm.documentType) {
      showErrorAction("DOCUMENT_REQUIRED");
      return;
    }
    if (!isAllowedDocumentFile(uploadForm.file)) {
      showErrorAction("DOCUMENT_TYPE");
      return;
    }
    if (uploadForm.file.size > MAX_DOCUMENT_BYTES) {
      showErrorAction("DOCUMENT_TOO_LARGE");
      return;
    }

    const formData = new FormData();
    formData.append("documentType", uploadForm.documentType);
    formData.append("document", uploadForm.file);

    try {
      setUploading(true);
      setUploadProgress(1);
      await agentApi.post(`/travel-partner/bookings/${id}/pilgrims/${passengerId}/documents`, {
        body: formData,
        onUploadProgress: (percent) => setUploadProgress(percent),
      });
      resetUploadModal();
      showSuccessAction("UPLOAD_DOCUMENT");
      reloadJourney().catch((reloadError) => {
        showApiError(reloadError, "DOCUMENT_REFRESH");
      });
    } catch (uploadError) {
      setUploading(false);
      setUploadProgress(0);
      showApiError(uploadError, "UPLOAD_DOCUMENT");
    }
  }

  async function reloadJourney() {
    const payload = await agentApi.get(`/travel-partner/bookings/${id}/journey`);
    setData(payload?.data || payload || {});
  }

  async function submitBooking() {
    if (!canMutateBookings) return;
    try {
      setSubmitting(true);
      await agentApi.post(`/travel-partner/bookings/${id}/submit`);
      await reloadJourney();
      showSuccessAction("SUBMIT_BOOKING");
    } catch (submitError) {
      showApiError(submitError, "SUBMIT_BOOKING");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelBooking() {
    if (!canMutateBookings) return;
    try {
      setCancelling(true);
      await agentApi.post(`/travel-partner/bookings/${id}/cancel`);
      await reloadJourney();
      showSuccessAction("CANCEL_BOOKING");
    } catch (cancelError) {
      showApiError(cancelError, "CANCEL_BOOKING");
    } finally {
      setCancelling(false);
    }
  }

  async function reloadChangeRequests() {
    const requestPayload = await agentApi.get(`/travel-partner/bookings/${id}/change-requests`);
    const requestData = unwrapData(requestPayload);
    const rows = asArray(requestData);
    setChangeRequests(rows);
    const latest = rows[0];
    if (latest?.request_id) {
      const detail = await agentApi.get(`/travel-partner/change-requests/${latest.request_id}`);
      const detailData = unwrapData(detail);
      setRequestHistory(asArray(detailData.history || detailData));
    } else {
      setRequestHistory([]);
    }
  }

  function openRequestModal(type) {
    if (!canMutateBookings) return;
    setRequestForm({ reason: "", changes: "" });
    setRequestModal(type);
  }

  async function submitChangeRequest() {
    if (!canMutateBookings) return;
    const reason = String(requestForm.reason || "").trim();
    if (reason.length < 8) {
      showErrorAction("REQUEST_REASON");
      return;
    }
    if (requestModal === "amendment" && !String(requestForm.changes || "").trim()) {
      showErrorAction("REQUEST_CHANGES");
      return;
    }
    try {
      setSavingRequest(true);
      const path = requestModal === "cancellation"
        ? `/travel-partner/bookings/${id}/cancellation-requests`
        : `/travel-partner/bookings/${id}/amendment-requests`;
      await agentApi.post(path, {
        body: {
          reason,
          requestedChanges: requestModal === "amendment" ? { summary: requestForm.changes } : undefined,
        },
      });
      setRequestModal(null);
      showSuccessAction(requestModal === "cancellation" ? "REQUEST_CANCELLATION" : "REQUEST_AMENDMENT");
      await reloadChangeRequests();
    } catch (requestError) {
      showApiError(requestError, requestModal === "cancellation" ? "REQUEST_CANCELLATION" : "REQUEST_AMENDMENT");
    } finally {
      setSavingRequest(false);
    }
  }

  async function addPassenger() {
    if (!canMutatePassengers) return;
    const packageId = booking.package_id || booking.upbu_pkg_id;
    if (!packageId) {
      showErrorAction("PASSENGER_PACKAGE_MISSING");
      return;
    }
    const validationError = validatePassenger(passengerForm);
    if (validationError) {
      showErrorAction(validationError);
      return;
    }
    if (passengerForm.uppd_image) {
      if (!isAllowedPhotoFile(passengerForm.uppd_image)) {
        showErrorAction("PASSENGER_PHOTO_TYPE");
        return;
      }
      if (passengerForm.uppd_image.size > MAX_PHOTO_BYTES) {
        showErrorAction("PASSENGER_PHOTO_TOO_LARGE");
        return;
      }
    }
    if (pilgrimLimit > 0 && pilgrims.length >= pilgrimLimit) {
      showErrorAction("PASSENGER_LIMIT");
      return;
    }
    try {
      setSavingPassenger(true);
      const formData = new FormData();
      Object.entries(passengerForm).forEach(([key, value]) => {
        if (key === "uppd_image") {
          if (value) formData.append("uppd_image", value);
          return;
        }
        formData.append(key, value == null ? "" : String(value));
      });
      await agentApi.post(`/umrah-packages/${packageId}/bookings/${id}/passengers`, { body: formData });
      setPassengerForm(emptyPassenger);
      showSuccessAction("ADD_PASSENGER");
      await reloadJourney();
    } catch (saveError) {
      showApiError(saveError, "ADD_PASSENGER");
    } finally {
      setSavingPassenger(false);
    }
  }

  async function removePassenger(passengerId) {
    if (!canMutatePassengers) return;
    const packageId = booking.package_id || booking.upbu_pkg_id;
    if (!packageId || !passengerId) return;
    try {
      setRemovingPassengerId(String(passengerId));
      await agentApi.delete(`/umrah-packages/${packageId}/bookings/${id}/passengers/${passengerId}`);
      showSuccessAction("REMOVE_PASSENGER");
      await reloadJourney();
    } catch (removeError) {
      showApiError(removeError, "REMOVE_PASSENGER");
    } finally {
      setRemovingPassengerId("");
    }
  }

  async function downloadReceipt(paymentId) {
    try {
      const blob = await agentApi.get(`/payments/${paymentId}/receipt`, { responseType: "blob" });
      downloadBlob(blob, `receipt-${paymentId}`);
      showSuccessAction("DOWNLOAD_RECEIPT");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_RECEIPT");
    }
  }

  async function requestRefund(paymentId) {
    if (!canMutateFinance) return;
    const reason = window.prompt("Refund request reason");
    if (!reason) return;
    try {
      await agentApi.post(`/payments/${paymentId}/refund-request`, { body: { reason } });
      showSuccessAction("REQUEST_REFUND");
    } catch (refundError) {
      showApiError(refundError, "REQUEST_REFUND");
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24 agent-booking-detail">
        <PageHeader
          eyebrow="Booking Detail"
          title={booking.booking_reference || `Booking ${id}`}
          description={!loading && data && canMutateBookings && isDraftBooking(booking.booking_status) ? getPageCopy("DRAFT_BOOKING") : undefined}
          items={[
            DASHBOARD_CRUMB,
            { label: "Bookings", href: "/bookings" },
            { label: booking.booking_reference || `Booking ${id}` },
          ]}
          backTo="/bookings"
          backStrict
          actions={
            !loading && data ? (
              <div className="page-toolbar">
                {canMutateBookings && isDraftBooking(booking.booking_status) ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={submitting || cancelling || savingRequest}
                    onClick={submitBooking}
                  >
                    {submitting ? "Submitting..." : "Submit request"}
                  </button>
                ) : null}
                {canMutateBookings && isAgentCancellable(booking.booking_status) ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={submitting || cancelling || savingRequest}
                    onClick={cancelBooking}
                  >
                    {cancelling ? "Cancelling..." : (isDraftBooking(booking.booking_status) ? "Discard draft" : "Cancel request")}
                  </button>
                ) : null}
                {canMutateBookings && canRequestBookingChange(booking.booking_status) ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={savingRequest || changeRequests.some((row) => row.request_type === "amendment" && row.status === "pending")}
                      onClick={() => openRequestModal("amendment")}
                    >
                      Request amendment
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      disabled={savingRequest || changeRequests.some((row) => row.request_type === "cancellation" && row.status === "pending")}
                      onClick={() => openRequestModal("cancellation")}
                    >
                      Request cancellation
                    </button>
                  </>
                ) : null}
              </div>
            ) : null
          }
        />
        {loading ? <PageLoader message="Loading booking..." /> : null}

        {!loading && !data ? (
          <EmptyState message="No booking details are available." />
        ) : null}

        {!loading && data ? (
          <>
            <div className="card border-0 shadow-sm mb-3 agent-booking-header-card">
              <div className="card-body">
                <div className="agent-booking-header-meta">
                  <FieldBlock label="Customer">{displayValue(booking.customer_name, "Unassigned")}</FieldBlock>
                  <FieldBlock label="Package">{displayValue(booking.package_name, "Not set")}</FieldBlock>
                  <FieldBlock label="Departure">{formatDate(booking.departure_date || booking.booking_date)}</FieldBlock>
                  <FieldBlock label="Pilgrims">{pilgrimCountLabel}</FieldBlock>
                  <FieldBlock label="Booking status">
                    <StatusBadge value={booking.booking_status}>
                      {formatBookingStatus(booking.booking_status)}
                    </StatusBadge>
                  </FieldBlock>
                  <FieldBlock label="Payment status">
                    <StatusBadge value={paymentStatus}>{formatPaymentStatus(paymentStatus)}</StatusBadge>
                  </FieldBlock>
                  <FieldBlock label="Document status">
                    <StatusBadge value={docs.value}>{docs.label}</StatusBadge>
                  </FieldBlock>
                  <FieldBlock label="Visa status">
                    <StatusBadge value={visas.value}>{visas.label}</StatusBadge>
                  </FieldBlock>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm agent-booking-panel-card">
              <div className="card-header agent-booking-tabbar">
                <Tabs
                  tabs={BOOKING_TABS}
                  activeId={activeTab}
                  onChange={setActiveTab}
                  className="agent-booking-tabs"
                  ariaLabel="Booking sections"
                  idPrefix="booking-tab"
                />
              </div>
              <div className="card-body">
                {activeTab === "overview" ? (
                  <div id="booking-tab-panel-overview" role="tabpanel" aria-labelledby="booking-tab-overview">
                    <h5 className="agent-booking-section-title">Booking summary</h5>
                    <div className="agent-booking-summary">
                      <FieldBlock label="Booked on">{formatDate(bookedOn)}</FieldBlock>
                      <FieldBlock label="Return">{returnDate ? formatDate(returnDate) : "Not set"}</FieldBlock>
                      <FieldBlock label="Booking total">{formatCurrency(bookingTotal)}</FieldBlock>
                      <FieldBlock label="Outstanding">{formatCurrency(outstandingAmount)}</FieldBlock>
                    </div>

                    <h5 className="agent-booking-section-title">Travel Readiness</h5>
                    <div className="agent-booking-readiness">
                      <SummaryTile label="Readiness">
                        <div className="agent-booking-field-value">
                          <StatusBadge value={readiness.readiness_status || "not_ready"} />
                        </div>
                        <div className="agent-booking-field-meta">{readiness.readiness_notes || "Pending assignment"}</div>
                      </SummaryTile>
                      <SummaryTile label="Flight">
                        <div className="agent-booking-field-value">
                          {flightStatus ? <StatusBadge value={flightStatus} /> : (travel.flight_airline_name || "Not assigned")}
                        </div>
                        <div className="agent-booking-field-meta">{flightMeta}</div>
                      </SummaryTile>
                      <SummaryTile label="Hotels">
                        <div className="agent-booking-field-value">
                          {hotelStatus ? <StatusBadge value={hotelStatus} /> : (travel.makkah_hotel_name || "Not assigned")}
                        </div>
                        <div className="agent-booking-field-meta">{hotelMeta}</div>
                      </SummaryTile>
                      <SummaryTile label="Transport">
                        <div className="agent-booking-field-value">
                          {transportStatus ? <StatusBadge value={transportStatus} /> : (travel.transport_type || "Not assigned")}
                        </div>
                        <div className="agent-booking-field-meta">{transportMeta}</div>
                      </SummaryTile>
                      <SummaryTile label="Payment">
                        <div className="agent-booking-field-value">
                          <StatusBadge value={paymentStatus}>{formatPaymentStatus(paymentStatus)}</StatusBadge>
                        </div>
                        <div className="agent-booking-field-meta">{formatCurrency(outstandingAmount ?? bookingTotal)}</div>
                      </SummaryTile>
                      <SummaryTile label="Itinerary">
                        <div className="agent-booking-field-value">{itinerary.length || 0} day(s)</div>
                        <div className="agent-booking-field-meta">{rooms.length || 0} room allocation(s)</div>
                      </SummaryTile>
                      <SummaryTile label="Voucher">
                        <div className="agent-booking-field-value">
                          <StatusBadge value={voucherStatus} />
                        </div>
                        <div className="agent-booking-field-meta">{voucherMeta}</div>
                      </SummaryTile>
                    </div>
                  </div>
                ) : null}

                {activeTab === "flights" ? (
                  <div id="booking-tab-panel-flights" role="tabpanel" aria-labelledby="booking-tab-flights">
                    <h5 className="agent-booking-section-title">Flights</h5>
                    {flights.length > 0 ? (
                    <Table>
                      <thead>
                        <tr>
                          <th>Airline</th>
                          <th>Flight number</th>
                          <th>Departure</th>
                          <th>Arrival</th>
                          <th>PNR</th>
                          <th>Status</th>
                          <th>Passengers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flights.map((row, index) => {
                          const isReturn = String(row.leg || row.leg_label || row.sector_type || "").toLowerCase().includes("return") || index > 0;
                          return (
                          <tr key={row.flight_id || row.btf_id || row.id || `${row.leg || row.flight_number || "flight"}-${index}`}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <iconify-icon
                                  icon={isReturn ? "mdi:airplane-landing" : "mdi:airplane-takeoff"}
                                  className="icon text-xl text-primary flex-shrink-0"
                                  title={isReturn ? "Landing" : "Take off"}
                                  aria-label={isReturn ? "Landing" : "Take off"}
                                ></iconify-icon>
                                <span>{displayValue(row.airline || firstValue(row, ["airline_name", "flight_airline_name", "airlines"]))}</span>
                              </div>
                            </td>
                            <td>{displayValue(firstValue(row, ["flight_number", "flight_code", "flight_no"]))}</td>
                            <td>
                              <div>{displayValue(placeValue(row.departure_airport) || (isReturn ? placeValue(travel.arrival_airport) : ""))}</div>
                              <div className="text-secondary small">{combineDateTime(firstValue(row, ["departure_datetime", "departure_at", "departure_date", "flight_departure_date"]), firstValue(row, ["departure_time", "flight_departure_time"]))}</div>
                            </td>
                            <td>
                              <div>{displayValue(placeValue(row.arrival_airport) || (isReturn ? placeValue(travel.departure_airport) : ""))}</div>
                              <div className="text-secondary small">{combineDateTime(firstValue(row, ["arrival_datetime", "arrival_at", "arrival_date", "flight_arrival_date"]), firstValue(row, ["arrival_time", "flight_arrival_time"]))}</div>
                            </td>
                            <td>{displayValue(firstValue(row, ["pnr", "flight_pnr"]))}</td>
                            <td>{firstValue(row, ["status", "flight_status"]) ? <StatusBadge value={firstValue(row, ["status", "flight_status"])} /> : "-"}</td>
                            <td>{displayValue(firstValue(row, ["passenger_name", "passengers", "passenger_count", "pax"]) || pilgrimCountLabel)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    ) : (
                      <EmptyState message="No flight assignment returned for this booking." />
                    )}
                  </div>
                ) : null}

                {activeTab === "hotels" ? (
                  <div id="booking-tab-panel-hotels" role="tabpanel" aria-labelledby="booking-tab-hotels">
                    <h5 className="agent-booking-section-title">Hotels</h5>
                    {hotels.length > 0 ? (
                      <div className="agent-booking-hotels">
                        {hotels.map((hotel, index) => {
                          const name = firstValue(hotel, ["hotel_name", "name", "hotel"]);
                          const image = firstValue(hotel, ["image", "hotel_image", "photo", "file_name", "uhl_image"]);
                          const status = hotelDisplayStatus(name, firstValue(hotel, ["confirmation_status", "status", "hotel_status"]));
                          const checkIn = hotel.check_in || hotel.checkIn;
                          const checkOut = hotel.check_out || hotel.checkOut;
                          const nights = firstValue(hotel, ["nights", "night_count", "no_of_nights"]) || nightsBetween(checkIn, checkOut);
                          const city = firstValue(hotel, ["city", "location", "city_name"]);
                          const stars = firstValue(hotel, ["stars", "uhl_stars"]);
                          const address = firstValue(hotel, ["address", "uhl_address"]);
                          return (
                            <article className="agent-booking-hotel-card" key={hotel.id || hotel.hotel_id || `${name}-${index}`}>
                              <div className="agent-hotel-photo">
                                <CatalogImage folder="umrah_hotel_list" fileName={image} alt={name || "Hotel"} height={200} />
                              </div>
                              <div className="agent-booking-hotel-body">
                                <div className="agent-booking-hotel-title">
                                 <div>
  <div className="agent-booking-field-value">
    {displayValue(name, "Not assigned")}
  </div>

  {city && (
    <div className="agent-booking-hotel-info mt-3">
       
      <span>City - {city}</span>
    </div>
  )}

  {stars && (
    <div className="agent-booking-hotel-info">
       
      <span>Ratings -{stars} star</span>
    </div>
  )}

  {/* {firstValue(hotel, ["property", "propertyType"]) && (
    <div className="agent-booking-hotel-info">
      
      <span>{firstValue(hotel, ["property", "propertyType"])}</span>
    </div>
  )} */}

  {address && (
    <div className="agent-booking-hotel-info">
      
      <span>Address -{address}</span>
    </div>
  )}
</div>
                                  <StatusBadge value={status || "not_assigned"} />
                                </div>

                                {/* <div className="agent-booking-hotel-section">
                                  <div className="agent-booking-hotel-section-title">Stay</div>
                                  <div className="agent-booking-hotel-meta">
                                    <FieldBlock label="Check-in">{formatDate(checkIn) || "-"}</FieldBlock>
                                    <FieldBlock label="Check-out">{formatDate(checkOut) || "-"}</FieldBlock>
                                    <FieldBlock label="Nights">{displayValue(nights)}</FieldBlock>
                                  </div>
                                </div>

                                <div className="agent-booking-hotel-section">
                                  <div className="agent-booking-hotel-section-title">Room</div>
                                  <div className="agent-booking-hotel-meta">
                                    <FieldBlock label="Room type">{displayValue(firstValue(hotel, ["room_type"]))}</FieldBlock>
                                    <FieldBlock label="Occupancy type">{displayValue(occupancyDisplay(firstValue(hotel, ["occupancy_type", "occupancyType"])))}</FieldBlock>
                                    <FieldBlock label="Rooms">{displayValue(firstValue(hotel, ["rooms", "room_count"]))}</FieldBlock>
                                  </div>
                                </div>

                                <div className="agent-booking-hotel-section">
                                  <div className="agent-booking-hotel-section-title">Booking</div>
                                  <div className="agent-booking-hotel-meta">
                                    <FieldBlock label="Meal plan">{displayValue(firstValue(hotel, ["meal_plan", "mealPlan"]))}</FieldBlock>
                                    <FieldBlock label="Confirmation">{displayValue(firstValue(hotel, ["confirmation", "confirmation_number", "confirmation_no", "reference", "hotel_reference"]))}</FieldBlock>
                                  </div>
                                </div> */}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ) : (
                      <EmptyState message="No hotel assignment returned for this booking." />
                    )}

                    {rooms.length > 0 ? (
                      <div className="mt-4">
                        <h6 className="agent-booking-section-title">Room allocation</h6>
                        <Table>
                          <thead>
                            <tr>
                              <th>Room</th>
                              <th>Occupancy type</th>
                              <th>Rooms</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rooms.map((room) => (
                              <tr key={room.btr_id || room.room_label}>
                                <td>{displayValue(room.room_label)}</td>
                                <td>{displayValue(occupancyDisplay(room.occupancy_type))}</td>
                                <td>{displayValue(room.room_count)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {activeTab === "transport" ? (
                  <div id="booking-tab-panel-transport" role="tabpanel" aria-labelledby="booking-tab-transport">
                    <h5 className="agent-booking-section-title">Transport</h5>
                    {transports.length > 0 ? (
                    <Table>
                      <thead>
                        <tr>
                          <th>Vehicle / service</th>
                          <th>Pickup</th>
                          <th>Drop</th>
                          <th>Date / time</th>
                          <th>Driver / provider</th>
                          <th>Reference</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transports.map((row, index) => (
                          <tr key={row.transport_id || row.btt_id || row.id || `transport-${index}`}>
                            <td>{displayValue(firstValue(row, ["vehicle", "vehicle_name", "transport_vendor_name", "vehicle_type", "transport_vehicle_type", "transport_type", "service"]))}</td>
                            <td>{displayValue(firstValue(row, ["pickup", "pickup_point", "transport_pickup_point"]))}</td>
                            <td>{displayValue(firstValue(row, ["drop", "drop_point", "transport_drop_point"]))}</td>
                            <td>{combineDateTime(firstValue(row, ["date", "transport_pickup_datetime", "pickup_datetime", "transport_date", "pickup_date", "datetime"]), firstValue(row, ["time", "pickup_time", "transport_time"]))}</td>
                            <td>{displayValue(firstValue(row, ["driver", "driver_name", "transport_driver_name", "transport_driver", "provider", "transport_provider", "transport_vendor_name"]))}</td>
                            <td>{displayValue(firstValue(row, ["reference", "transport_vehicle_number", "vehicle_number", "transport_reference", "confirmation", "booking_reference"]))}</td>
                            <td>{firstValue(row, ["status", "transport_status"]) ? <StatusBadge value={firstValue(row, ["status", "transport_status"])} /> : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    ) : (
                      <EmptyState message="No transport assignment returned for this booking." />
                    )}
                  </div>
                ) : null}

                {activeTab === "pilgrims" ? (
                  <div id="booking-tab-panel-pilgrims" role="tabpanel" aria-labelledby="booking-tab-pilgrims">
                    {canAddPilgrim ? (
                    <>
                    <div className="agent-booking-section-head">
                      <h5 className="agent-booking-section-title mb-0">Add passenger</h5>
                      {pilgrimLimit > 0 ? (
                        <div className="agent-booking-field-meta mb-0">{pilgrims.length} of {pilgrimLimit} added</div>
                      ) : null}
                    </div>
                    <p className="small text-secondary mb-3">First name, last name, passport number, and an expiry at least 6 months away are required.</p>
                    <div className="agent-passenger-form">
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Title</label>
                        <select className="form-select" value={passengerForm.uppd_title} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_title: event.target.value })}>
                          <option>Mr</option>
                          <option>Mrs</option>
                          <option>Ms</option>
                        </select>
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">First name</label>
                        <input className="form-control" value={passengerForm.uppd_fname} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_fname: event.target.value })} />
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Last name</label>
                        <input className="form-control" value={passengerForm.uppd_lname} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_lname: event.target.value })} />
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Gender</label>
                        <select className="form-select" value={passengerForm.uppd_gender} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_gender: event.target.value })}>
                          <option>Male</option>
                          <option>Female</option>
                        </select>
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Date of birth</label>
                        <input type="date" className="form-control" value={passengerForm.uppd_dob} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_dob: event.target.value })} />
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Passport number</label>
                        <input className="form-control" value={passengerForm.uppd_passport_no} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_passport_no: event.target.value })} />
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Passport expiry</label>
                        <input type="date" className="form-control" value={passengerForm.uppd_passport_expiry} onChange={(event) => setPassengerForm({ ...passengerForm, uppd_passport_expiry: event.target.value })} />
                      </div>
                      <div className="agent-passenger-form__field">
                        <label className="form-label">Photo</label>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                          className="form-control"
                          onChange={(event) => setPassengerForm({ ...passengerForm, uppd_image: event.target.files?.[0] || null })}
                        />
                      </div>
                    </div>
                    <div className="agent-passenger-form__actions">
                      <button type="button" className="btn btn-primary" disabled={savingPassenger} onClick={addPassenger}>
                        {savingPassenger ? "Saving..." : "Add passenger"}
                      </button>
                    </div>
                    <BulkPassengerUpload
                      packageId={booking.package_id || booking.upbu_pkg_id}
                      bookingId={id}
                      remainingSlots={remainingSlots}
                      onImported={reloadJourney}
                    />
                    </>
                    ) : canMutatePassengers && statusAllowsPassengerEdit ? (
                    <div className="agent-booking-tile mb-4">
                      <div className="agent-booking-field-value">Passenger list complete</div>
                      <div className="agent-booking-field-meta mb-0">
                        All {pilgrimLimit} pilgrims have been added for this booking.
                      </div>
                    </div>
                    ) : null}

                    <h5 className="agent-booking-section-title">Pilgrims</h5>
                    {pilgrims.length > 0 ? (
                    <Table>
                      <thead>
                        <tr>
                          <th>Passenger name</th>
                          <th>Passport</th>
                          <th>DOB</th>
                          <th>Gender</th>
                          <th>Visa</th>
                          <th>Documents</th>
                          <th>Required documents</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pilgrims.map((pilgrim) => (
                          <tr key={pilgrim.passenger_id}>
                            <td><PersonCell name={pilgrim.passenger_name} /></td>
                            <td>{displayValue(pilgrim.uppd_passport_no, "No passport")}</td>
                            <td>{pilgrim.uppd_dob || pilgrim.date_of_birth ? formatDate(pilgrim.uppd_dob || pilgrim.date_of_birth) : "-"}</td>
                            <td>{displayValue(pilgrim.uppd_gender || pilgrim.gender)}</td>
                            <td><StatusBadge value={pilgrim.visa_status || "draft"} /></td>
                            <td><StatusBadge value={pilgrimDocumentStatus(pilgrim)} /></td>
                            <td>{displayValue(firstValue(pilgrim, ["required_documents", "pending_documents", "action_required_documents", "total_documents"]))}</td>
                            <td>
                              <div className="d-inline-flex align-items-center gap-2">
                                {canMutateDocuments ? (
                                <button
                                  type="button"
                                  className="agent-icon-btn"
                                  title="Upload document"
                                  aria-label={`Upload document for ${pilgrim.passenger_name || "pilgrim"}`}
                                  onClick={() => openUploadModal(pilgrim)}
                                >
                                  <iconify-icon icon="solar:upload-minimalistic-outline"></iconify-icon>
                                </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="agent-icon-btn"
                                  title="View documents"
                                  aria-label={`View documents for ${pilgrim.passenger_name || "pilgrim"}`}
                                  onClick={() => openPreviewModal(pilgrim)}
                                >
                                  <iconify-icon icon="solar:eye-outline"></iconify-icon>
                                </button>
                                {canMutatePassengers && statusAllowsPassengerEdit ? (
                                  <button
                                    type="button"
                                    className="agent-icon-btn"
                                    title="Remove passenger"
                                    aria-label={`Remove ${pilgrim.passenger_name || "passenger"}`}
                                    disabled={removingPassengerId === String(pilgrim.passenger_id)}
                                    onClick={() => removePassenger(pilgrim.passenger_id)}
                                  >
                                    <iconify-icon icon="solar:trash-bin-minimalistic-outline"></iconify-icon>
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    ) : (
                      <EmptyState message="No pilgrims on this booking." />
                    )}
                  </div>
                ) : null}

                {activeTab === "itinerary" ? (
                  <div id="booking-tab-panel-itinerary" role="tabpanel" aria-labelledby="booking-tab-itinerary">
                    <h5 className="agent-booking-section-title">Itinerary</h5>
                    {itinerary.length > 0 ? (
                    <Table>
                      <thead>
                        <tr>
                          <th>Day</th>
                          {showItineraryDate ? <th>Date</th> : null}
                          <th>Location</th>
                          {showItineraryActivity ? <th>Activity</th> : null}
                          {showItineraryHotel ? <th>Hotel</th> : null}
                          {showItineraryTransport ? <th>Transport</th> : null}
                          <th>Notes</th>
                          <th>Preview</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itinerary.map((item, index) => {
                          const dayLabel = displayValue(firstValue(item, ["day_label", "day", "day_number", "sort_order"]));
                          const notesHtml = itineraryNotesHtml(item);
                          const notesPlain = stripHtml(notesHtml);
                          const notesPreviewText = previewText(notesHtml);
                          return (
                          <tr key={item.bti_id || item.sort_order || `day-${index}`}>
                            <td>{dayLabel}</td>
                            {showItineraryDate ? <td>{displayValue(itineraryDateDisplay(item))}</td> : null}
                            <td>{displayValue(firstValue(item, ["city", "location", "place"]))}</td>
                            {showItineraryActivity ? <td className="text-break">{displayValue(itineraryActivityValue(item))}</td> : null}
                            {showItineraryHotel ? <td>{displayValue(itineraryHotelValue(item))}</td> : null}
                            {showItineraryTransport ? <td>{displayValue(itineraryTransportValue(item))}</td> : null}
                            <td>
                              {notesPreviewText ? (
                                <span className="agent-itinerary-notes-preview" title={notesPlain}>{notesPreviewText}</span>
                              ) : "-"}
                            </td>
                            <td>
                              {notesPreviewText ? (
                                <button
                                  type="button"
                                  className="agent-icon-btn"
                                  title="View notes"
                                  aria-label={`View notes for ${dayLabel}`}
                                  onClick={() => setNotesPreview({
                                    title: `${dayLabel} notes`,
                                    ...notesToBlocks(notesHtml),
                                  })}
                                >
                                  <iconify-icon icon="solar:eye-outline"></iconify-icon>
                                </button>
                              ) : "-"}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    ) : (
                      <EmptyState message="No itinerary published yet." />
                    )}
                  </div>
                ) : null}

                {activeTab === "payment" ? (
                  <div id="booking-tab-panel-payment" role="tabpanel" aria-labelledby="booking-tab-payment">
                    <div className="agent-booking-section-head">
                      <h5 className="agent-booking-section-title mb-0">Payment</h5>
                      {canMutateFinance && canUploadPaymentProof(booking.booking_status, paymentStatus) ? (
                        <div className="d-flex flex-wrap gap-2">
                          <Link to={`/payment-proof?bookingId=${id}&method=wallet`} className="btn btn-primary btn-sm">Pay from wallet</Link>
                          <Link to={`/payment-proof?bookingId=${id}`} className="btn btn-outline-primary btn-sm">Upload Proof</Link>
                        </div>
                      ) : null}
                    </div>
                    <div className="agent-booking-payment-summary">
                      <SummaryTile label="Booking total">
                        <div className="agent-booking-field-value">{formatCurrency(bookingTotal)}</div>
                      </SummaryTile>
                      <SummaryTile label="Paid">
                        <div className="agent-booking-field-value">{formatCurrency(paymentSummary?.successful_payments ?? paymentSummary?.successfulPayments)}</div>
                      </SummaryTile>
                      <SummaryTile label="Outstanding">
                        <div className="agent-booking-field-value">{formatCurrency(outstandingAmount)}</div>
                      </SummaryTile>
                      <SummaryTile label="Pending">
                        <div className="agent-booking-field-value">{formatCurrency(paymentSummary?.pending_payments ?? paymentSummary?.pendingPayments)}</div>
                      </SummaryTile>
                      <SummaryTile label="Payment status">
                        <div className="agent-booking-field-value"><StatusBadge value={paymentStatus}>{formatPaymentStatus(paymentStatus)}</StatusBadge></div>
                      </SummaryTile>
                    </div>
                    <p className="agent-booking-field-meta mb-3">
                      {summary.document_release?.payment_released
                        ? "Payment is complete. Issued visa files are released. Passport and photos stay available for processing."
                        : "Passport and photos stay available for processing. Issued visa files stay locked until this booking payment is complete."}
                    </p>
                    {paymentHistory.length > 0 ? (
                    <Table>
                      <thead>
                        <tr>
                          <th>Payment date</th>
                          <th>Reference</th>
                          <th className="text-end">Amount</th>
                          <th>Method</th>
                          <th>Status</th>
                          <th className="text-end">Receipt / actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentHistory.map((row) => (
                          <tr key={row.transaction_id}>
                            <td>{firstValue(row, ["transaction_date", "payment_date", "created_at"]) ? formatDate(firstValue(row, ["transaction_date", "payment_date", "created_at"])) : "-"}</td>
                            <td className="text-break">{row.reference_number || row.booking_reference || row.transaction_id}</td>
                            <td className="text-end">{formatCurrency(row.amount)}</td>
                            <td>{displayValue(firstValue(row, ["payment_method", "method", "paymentMethod"]))}</td>
                            <td><StatusBadge value={row.status || "pending"}>{formatPaymentStatus(row.status)}</StatusBadge></td>
                            <td className="text-end">
                              <div className="agent-booking-inline-actions">
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => downloadReceipt(row.transaction_id)}>Receipt</button>
                                {canMutateFinance && String(row.transaction_kind || "payment") !== "refund" ? (
                                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => requestRefund(row.transaction_id)}>Refund</button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    ) : (
                      <EmptyState message="No payments recorded for this booking." />
                    )}
                  </div>
                ) : null}

                {activeTab === "voucher" ? (
                  <div id="booking-tab-panel-voucher" role="tabpanel" aria-labelledby="booking-tab-voucher">
                    <div className="agent-booking-section-head">
                      <h5 className="agent-booking-section-title mb-0">Voucher</h5>
                      <Link to={`/bookings/${id}/voucher`} className="btn btn-outline-primary btn-sm">
                        View Voucher
                      </Link>
                    </div>
                    {voucherPublished ? (
                      <div className="agent-booking-readiness agent-booking-summary--3">
                        <SummaryTile label="Voucher status">
                          <div className="agent-booking-field-value"><StatusBadge value={voucherStatus} /></div>
                        </SummaryTile>
                        <SummaryTile label="Voucher reference">
                          <div className="agent-booking-field-value">{displayValue(firstValue(travel, ["voucher_reference", "voucher_no", "voucher_number", "voucher_code", "voucher_title"]))}</div>
                        </SummaryTile>
                        <SummaryTile label="Generated date">
                          <div className="agent-booking-field-value">
                            {firstValue(travel, ["voucher_generated_at", "generated_at", "voucher_created_at", "voucher_published_at", "published_at", "voucher_date", "published_date"])
                              ? formatDate(firstValue(travel, ["voucher_generated_at", "generated_at", "voucher_created_at", "voucher_published_at", "published_at", "voucher_date", "published_date"]))
                              : "-"}
                          </div>
                        </SummaryTile>
                        <SummaryTile label="Published status">
                          <div className="agent-booking-field-value"><StatusBadge value={voucherPublished ? "published" : "not_published"} /></div>
                        </SummaryTile>
                      </div>
                    ) : (
                      <div className="agent-booking-empty">
                        <div className="mb-2"><StatusBadge value={voucherStatus} /></div>
                        <div className="agent-booking-field-value mb-1">Not published yet</div>
                        <div className="agent-booking-field-meta mb-0">{travel.voucher_title || "A travel voucher has not been published for this booking."}</div>
                      </div>
                    )}
                  </div>
                ) : null}

                {activeTab === "activity" ? (
                  <div id="booking-tab-panel-activity" role="tabpanel" aria-labelledby="booking-tab-activity">
                    <h5 className="agent-booking-section-title">Activity</h5>
                    {timeline.length > 0 ? (
                    <Table>
                      <thead>
                        <tr>
                          <th>Activity / event</th>
                          <th>Date / time</th>
                          <th>User / system</th>
                          <th>Status</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timeline.map((item) => {
                          const status = activityStatus(item);
                          return (
                            <tr key={item.event_id}>
                              <td className="agent-booking-field-value">{activityEventLabel(item)}</td>
                              <td>{activityDateTime(item)}</td>
                              <td>{activityActor(item)}</td>
                              <td>{status ? <StatusBadge value={status} /> : "-"}</td>
                              <td className="text-break">{displayValue(firstValue(item, ["notes", "description", "message"]), "No notes")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    ) : (
                      <EmptyState message="No workflow events returned." />
                    )}
                  </div>
                ) : null}

                {activeTab === "requests" ? (
                  <div id="booking-tab-panel-requests" role="tabpanel" aria-labelledby="booking-tab-requests">
                    <h5 className="agent-booking-section-title">Amendment and cancellation requests</h5>
                    {changeRequests.length > 0 ? (
                      <Table>
                        <thead>
                          <tr>
                            <th>Request</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Reason</th>
                            <th>Submitted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {changeRequests.map((row) => (
                            <tr key={row.request_id}>
                              <td>{row.request_number}</td>
                              <td>{formatLabel(row.request_type)}</td>
                              <td><StatusBadge value={row.status} /></td>
                              <td className="text-break">{displayValue(row.reason)}</td>
                              <td>{formatDateTime(row.created_date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <EmptyState message="No amendment or cancellation requests yet." />
                    )}
                    {requestHistory.length > 0 ? (
                      <div className="mt-4">
                        <h6>Latest request history</h6>
                        <Table>
                          <thead>
                            <tr>
                              <th>Event</th>
                              <th>Status</th>
                              <th>By</th>
                              <th>When</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {requestHistory.map((item) => (
                              <tr key={item.event_id}>
                                <td>{formatLabel(item.event_type)}</td>
                                <td>{item.to_status ? <StatusBadge value={item.to_status} /> : "-"}</td>
                                <td>{displayValue(item.actor_name)}</td>
                                <td>{formatDateTime(item.created_date)}</td>
                                <td className="text-break">{displayValue(item.notes)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
      <Modal
        open={Boolean(requestModal)}
        title={requestModal === "cancellation" ? "Request cancellation" : "Request amendment"}
        onClose={() => setRequestModal(null)}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" disabled={savingRequest} onClick={() => setRequestModal(null)}>
              Close
            </button>
            <button type="button" className="btn btn-primary" disabled={savingRequest} onClick={submitChangeRequest}>
              {savingRequest ? "Submitting..." : "Submit request"}
            </button>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Reason</label>
            <textarea
              className="form-control"
              rows={3}
              value={requestForm.reason}
              onChange={(event) => setRequestForm((current) => ({ ...current, reason: event.target.value }))}
              placeholder="Why is this change needed?"
            />
          </div>
          {requestModal === "amendment" ? (
            <div className="col-12">
              <label className="form-label">Requested changes</label>
              <textarea
                className="form-control"
                rows={4}
                value={requestForm.changes}
                onChange={(event) => setRequestForm((current) => ({ ...current, changes: event.target.value }))}
                placeholder="Describe the occupancy, date, passenger, or hotel change."
              />
            </div>
          ) : (
            <p className="small text-secondary mb-0">Operations must approve this. The confirmed booking will not change until then.</p>
          )}
        </div>
      </Modal>
      <Modal
        open={Boolean(notesPreview)}
        title={notesPreview?.title || "Itinerary notes"}
        className="agent-notes-modal"
        dialogClassName="modal-dialog-scrollable"
        onClose={() => setNotesPreview(null)}
        footer={(
          <button type="button" className="btn btn-primary" onClick={() => setNotesPreview(null)}>
            Close
          </button>
        )}
      >
        <div className="agent-itinerary-notes-html">
          {notesPreview?.heading ? <h6 className="agent-itinerary-notes-heading">{notesPreview.heading}</h6> : null}
          {(notesPreview?.paragraphs || []).length ? (
            notesPreview.paragraphs.map((paragraph, index) => (
              <p key={`${notesPreview.title}-${index}`}>{paragraph}</p>
            ))
          ) : (
            !notesPreview?.heading ? <p>No notes</p> : null
          )}
        </div>
      </Modal>
      <Modal
        open={Boolean(uploadTarget)}
        title={uploadTarget?.passengerName ? `Upload document · ${uploadTarget.passengerName}` : "Upload document"}
        onClose={closeUploadModal}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" disabled={uploading} onClick={closeUploadModal}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={uploading} onClick={uploadDocument}>
              {uploading ? "Updating..." : "Update"}
            </button>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Document type</label>
            <AgentSelect
              value={uploadForm.documentType}
              options={DOCUMENT_TYPE_OPTIONS}
              onChange={(value) => setUploadForm((current) => ({ ...current, documentType: value }))}
              isSearchable={false}
              isDisabled={uploading}
              placeholder="Choose document type"
            />
          </div>
          <div className="col-12">
            <label className="form-label">File</label>
            <input
              key={uploadTarget?.passengerId || "upload-closed"}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              className="form-control"
              disabled={uploading}
              onChange={(event) => setUploadForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
            />
          </div>
          {uploading ? (
            <div className="col-12">
              <div className="agent-upload-progress">
                <div className="agent-upload-progress-label">
                  <span>{uploadProgress >= 100 ? "Saving document..." : "Uploading document..."}</span>
                  <span>{Math.min(uploadProgress, 100)}%</span>
                </div>
                <div className="progress">
                  <div
                    className={`progress-bar${uploadProgress >= 100 ? " progress-bar-striped progress-bar-animated" : ""}`}
                    role="progressbar"
                    style={{ width: `${Math.max(uploadProgress, 4)}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
      <Modal
        open={Boolean(previewTarget)}
        title="Document preview"
        className="agent-document-preview-modal"
        dialogClassName="modal-dialog-scrollable"
        onClose={closePreviewModal}
        footer={(
          <button type="button" className="btn btn-primary" onClick={closePreviewModal}>
            Close
          </button>
        )}
      >
        {previewLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
          </div>
        ) : previewFiles.length > 0 ? (
          <div className="agent-document-preview">
            {isImageFile(previewFiles[previewIndex]?.mimeType, previewFiles[previewIndex]?.fileName) ? (
              <img src={previewFiles[previewIndex].url} alt="" />
            ) : (
              <iframe title="Document preview" src={previewFiles[previewIndex].url} />
            )}
            {previewFiles.length > 1 ? (
              <div className="agent-document-preview-nav">
                <button
                  type="button"
                  className="agent-icon-btn"
                  title="Previous document"
                  aria-label="Previous document"
                  disabled={previewIndex <= 0}
                  onClick={() => setPreviewIndex((current) => Math.max(0, current - 1))}
                >
                  <iconify-icon icon="solar:alt-arrow-left-outline"></iconify-icon>
                </button>
                <span>{previewIndex + 1} / {previewFiles.length}</span>
                <button
                  type="button"
                  className="agent-icon-btn"
                  title="Next document"
                  aria-label="Next document"
                  disabled={previewIndex >= previewFiles.length - 1}
                  onClick={() => setPreviewIndex((current) => Math.min(previewFiles.length - 1, current + 1))}
                >
                  <iconify-icon icon="solar:alt-arrow-right-outline"></iconify-icon>
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-secondary mb-0">No documents uploaded yet.</p>
        )}
      </Modal>
    </AgentLayout>
  );
}
